import { useMemo, useState } from "react";
import {
  api,
  type BenchEntry,
  type BenchNote,
  type ConsultantHistoryRow,
  type MyProfile,
} from "../lib/api";
import { useAction, useResource } from "../lib/useResource";
import { AppShell, ScreenHead } from "../shell/AppShell";
import { useSession, roleLabel } from "../lib/session";
import {
  Button,
  Empty,
  Failed,
  Field,
  Label,
  Mark,
  SkeletonRows,
  TextArea,
  AbsentNotice,
} from "../components/primitives";
import { day } from "../lib/format";

/**
 * The bench — who the firm can call on, and how it went last time.
 *
 * Hybrid-managed, and the split is the design rather than an accident of
 * two tables. The consultant writes the public half: what they do, what
 * they are free for. A&C owns the private half: a rating per engagement,
 * classified Restricted. They live in different tables with different
 * classifications, which is what keeps them apart — not a flag, not a
 * convention, not a filter in a query somebody might forget.
 *
 * **One screen, two readings, and nothing here decides which.**
 * `consultant_profile_read` admits the consultant themselves and the firm's
 * three senior roles, and `app.find_consultants` is SECURITY INVOKER — so a
 * consultant opening this gets exactly one entry, their own, and a Partner
 * gets everybody. The ratings behave the same way: they come from
 * `consultant_evaluation` through a SECURITY INVOKER query, so a consultant
 * reading their own history gets the engagements with the ratings **null**,
 * and the firm gets the numbers.
 *
 * That last point is a live product question — *may a consultant read their
 * own evaluation?* — which PROJECT_STATUS has carried as blocked on the
 * firm since Slice 2. It is answered here by the policy already in place
 * and by no branch in this file, deliberately. A screen that re-implemented
 * the rule would be a second place to get it wrong, and the day the firm
 * answers it, the answer is a migration and this file does not change.
 */

export function Bench() {
  const { me } = useSession();

  const [filter, setFilter] = useState("");
  const [applied, setApplied] = useState<string[]>([]);
  // Free text, separate from the tags: `query` is what is typed, `search` is
  // what has been submitted. Kept apart so the list does not refetch on every
  // keystroke — a request per character is somebody else's database.
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const bench = useResource<BenchEntry[]>(() => {
    const params = new URLSearchParams();
    for (const e of applied) params.append("expertise", e);
    if (search) params.set("q", search);
    const qs = params.toString();
    return api.get<BenchEntry[]>("/api/bench" + (qs ? `?${qs}` : ""));
  }, [applied, search]);

  const entries = bench.resource.state === "ready" ? bench.resource.data : [];
  const written = entries.filter((e) => e.hasEntry).length;

  // Every expertise anyone has listed, for the filter. Built from what came
  // back rather than from a fixed vocabulary, because the firm's practice
  // areas are not this screen's to decide.
  const vocabulary = useMemo(() => {
    const all = new Set<string>();
    for (const e of entries) for (const x of e.expertise ?? []) all.add(x);
    return [...all].sort();
  }, [entries]);

  return (
    <AppShell>
      <ScreenHead title="Bench" />

      <p className="max-w-[62ch] text-base text-secondary">
        Find the people available to support the firm&apos;s work. The list
        reflects what your role allows you to see.
      </p>

      <div className="mt-8">
          <Label as="h2">Find someone</Label>

          {/* Two different questions, so two controls rather than one that
              half-does both. Free text searches what people WROTE — their
              headline, background and expertise — and is how you look for
              somebody when you do not know the firm's vocabulary. The tags
              below are an exact match on a listed expertise, which is how you
              look for a skill once you do. */}
          <form
            className="mt-3 flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(query.trim());
            }}
          >
            <Field
              label="Search"
              hint="Name, headline, background or expertise."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
            />
            <Button type="submit" tone="quiet">
              Search
            </Button>
            {search && (
              <Button
                type="button"
                tone="quiet"
                onClick={() => {
                  setQuery("");
                  setSearch("");
                }}
              >
                Clear
              </Button>
            )}
          </form>

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <Field
              label="Expertise"
              hint="One at a time. Matching is on any of them, not all."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && filter.trim()) {
                  setApplied((v) => [...new Set([...v, filter.trim()])]);
                  setFilter("");
                }
              }}
            />
            <Button
              tone="quiet"
              disabled={!filter.trim()}
              onClick={() => {
                setApplied((v) => [...new Set([...v, filter.trim()])]);
                setFilter("");
              }}
            >
              Add
            </Button>
          </div>

          {applied.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
              {applied.map((x) => (
                <button
                  key={x}
                  type="button"
                  onClick={() => setApplied((v) => v.filter((y) => y !== x))}
                  className="label underline underline-offset-4 hover:text-ink"
                  title="Remove"
                >
                  {x} ×
                </button>
              ))}
            </div>
          )}

          {vocabulary.length > 0 && applied.length === 0 && (
            <p className="mt-3 text-sm text-secondary">
              In the directory: {vocabulary.join(" · ")}
            </p>
          )}
      </div>

      {bench.resource.state === "loading" && <SkeletonRows rows={4} />}
      {bench.resource.state === "failed" && (
        <Failed
          message={bench.resource.message}
          correlationId={bench.resource.correlationId}
        />
      )}

      {/* 404 from /api/bench, which since 051 is what a consultant or a client
          gets: `app.may_see_bench()` refuses them by name. The rail does not
          offer them the item, so arriving here means typing the URL — and the
          answer says nothing about who is on the bench or that it exists. */}
      {bench.resource.state === "absent" && <AbsentNotice />}

      {bench.resource.state === "ready" && (
        <section className="mt-10">
          <Label as="h2">
            {entries.length} on the bench
            {written < entries.length &&
              ` · ${entries.length - written} with nothing written`}
          </Label>

          {entries.length === 0 ? (
            <Empty
              title={
                applied.length || search ? "Nobody matches that" : "Nobody here yet"
              }
            >
              {applied.length || search
                ? "Try a broader term, or clear the search."
                : "Invite someone in People and they appear here, whether or not they have written anything about themselves."}
            </Empty>
          ) : (
            <ul className="mt-4">
              {entries.map((e) => (
                <BenchRow
                  key={e.userId}
                  entry={e}
                  canWrite={me?.role === "super_admin"}
                  open={openId === e.userId}
                  onToggle={() => setOpenId(openId === e.userId ? null : e.userId)}
                  onChanged={bench.reload}
                />
              ))}
            </ul>
          )}
        </section>
      )}
    </AppShell>
  );
}

/**
 * One person on the bench, entry or no entry.
 *
 * Somebody with nothing written is the most useful row here, not the least:
 * they are the gap. Before 045 they were invisible — the directory inner-
 * joined the profile — so the screen opened empty on a new firm and offered
 * no way out of empty, which is what the user hit.
 */
function BenchRow({
  entry,
  canWrite,
  open,
  onToggle,
  onChanged,
}: {
  entry: BenchEntry;
  /** Super Admin. The policy has always allowed it; nothing offered it. */
  canWrite: boolean;
  open: boolean;
  onToggle: () => void;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);

  return (
    <li className="border-b border-line py-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-5 gap-y-2">
        <div className="min-w-0">
          <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={open}
              className="text-left text-base text-ink underline-offset-4 hover:underline"
            >
              {entry.fullName}
            </button>
            {/* The role, shown because since 051 the bench is the whole firm
                and "who is this" is now a real question on the row. A Lead
                only ever sees "Consultant" here, which is itself the rank
                rule being visible rather than hidden. */}
            <Mark tone={entry.role === "consultant" ? "rest" : "live"}>
              {roleLabel(entry.role)}
            </Mark>
            {entry.notes > 0 && (
              <span className="text-xs text-muted">
                {entry.notes} note{entry.notes === 1 ? "" : "s"}
              </span>
            )}
          </span>
          {entry.headline ? (
            <p className="mt-1 max-w-[62ch] text-sm text-secondary">
              {entry.headline}
            </p>
          ) : (
            <p className="mt-1 text-sm text-secondary">
              {entry.email}
              <span aria-hidden> · </span>
              <span className="text-ink">nothing written yet</span>
            </p>
          )}
        </div>

        <div className="flex items-baseline gap-4">
          {entry.available && (
            <p className="text-sm text-secondary">{entry.available}</p>
          )}
          {canWrite && (
            <Button tone="quiet" onClick={() => setEditing((v) => !v)}>
              {entry.hasEntry ? "Edit" : "Write their entry"}
            </Button>
          )}
        </div>
      </div>

      {entry.expertise && entry.expertise.length > 0 && (
        <p className="mt-2 text-sm text-secondary">
          {entry.expertise.join(" · ")}
        </p>
      )}

      {editing && (
        <div className="mt-4 border-l-2 border-line pl-4">
          <p className="max-w-[62ch] text-sm text-secondary">
            Filling this in for {entry.fullName.split(" ")[0]}. They can
            change it themselves whenever they sign in — this is so the bench
            is useful before they get round to it.
          </p>
          <ProfileForm
            key={entry.userId + String(entry.hasEntry)}
            initial={{
              headline: entry.headline,
              expertise: entry.expertise,
              bio: entry.bio,
              availability: entry.available,
            }}
            path={`/api/consultants/${entry.userId}/profile`}
            onSaved={() => {
              setEditing(false);
              onChanged();
            }}
          />
        </div>
      )}

      {open && (
        <>
          <Notes subjectId={entry.userId} name={entry.fullName} onAdded={onChanged} />
          <History consultantId={entry.userId} />
        </>
      )}
    </li>
  );
}

/**
 * What they have worked on, and how it went.
 *
 * The rows are filtered by `assignment_read`, so a Lead sees this person's
 * history on engagements they have standing on — not the whole career. A
 * partial history is the correct answer to a partial authority; the note
 * below says so, because a list that is quietly short reads as a list that
 * is complete.
 */
function History({ consultantId }: { consultantId: string }) {
  const history = useResource<ConsultantHistoryRow[]>(
    () =>
      api.get<ConsultantHistoryRow[]>(`/api/consultants/${consultantId}/history`),
    [consultantId],
  );

  if (history.resource.state === "loading") return <SkeletonRows rows={2} />;
  if (history.resource.state === "failed") {
    return (
      <Failed
        message={history.resource.message}
        correlationId={history.resource.correlationId}
      />
    );
  }

  const rows = history.resource.state === "ready" ? history.resource.data : [];
  if (rows.length === 0) {
    return (
      <p className="mt-4 border-l-2 border-line pl-4 text-sm text-secondary">
        No engagements you can see. They may have worked on others.
      </p>
    );
  }

  return (
    <div className="mt-4 border-l-2 border-line pl-4">
      <ul>
        {rows.map((r) => (
          <li
            key={`${r.engagementId}-${r.started ?? ""}`}
            className="flex flex-wrap items-baseline justify-between gap-x-5 gap-y-1 py-2 text-sm"
          >
            <span className="text-ink">{r.engagement}</span>
            <span className="text-secondary">
              {r.scope === "engagement"
                ? "Whole engagement"
                : r.scope === "workstream"
                  ? "One workstream"
                  : "Assigned tasks only"} · {r.status}
              {r.started && ` · from ${day(r.started)}`}
              {r.ended && ` to ${day(r.ended)}`}
            </span>
            <span>
              {r.rating === null ? (
                <span className="text-secondary">not rated</span>
              ) : (
                <>
                  <Mark tone={r.rating >= 4 ? "success" : r.rating <= 2 ? "danger" : "warning"}>
                    {r.rating}/5 work
                  </Mark>{" "}
                  <span className="ml-2 text-secondary">
                    {r.timeliness}/5 on time
                  </span>
                </>
              )}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-sm text-secondary">
        Only engagements available to you are shown. Ratings are private to
        the firm and are never shown to the consultant.
      </p>
    </div>
  );
}

/*
 * `MyEntry` used to live here: the consultant's own editor, bolted onto the
 * bottom of the directory because /bench was the only route a consultant had.
 * It is gone, replaced by `ProfileDialog` opened from the identity box in the
 * rail — which every firm-side role can reach from any screen, and which is
 * what the Lead and the Partner never had. `ProfileForm` below stays: a Super
 * Admin still writes somebody else's entry through it, inline on the row.
 */

function ProfileForm({
  initial,
  path,
  onSaved,
}: {
  initial: MyProfile | null;
  /**
   * Where to PUT it. Your own entry goes to /api/me/profile, which takes no
   * id at all and cannot be pointed at somebody else; an administrator
   * writing for a consultant goes to that consultant's own path, where the
   * policy decides. Two routes because they are two different acts with
   * two different answers to "who may do this".
   */
  path: string;
  onSaved: () => void;
}) {
  const [headline, setHeadline] = useState(initial?.headline ?? "");
  const [expertise, setExpertise] = useState((initial?.expertise ?? []).join(", "));
  const [bio, setBio] = useState(initial?.bio ?? "");
  const [availability, setAvailability] = useState(initial?.availability ?? "");

  const save = useAction(async () => {
    // PUT, not PATCH, and the form submits every field each time: the
    // upsert behind it writes every column, so a field left out is a field
    // cleared. Sending the whole entry is the only honest way to use it.
    await api.put(path, {
      headline: headline.trim() || undefined,
      expertise: expertise
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean),
      bio: bio.trim() || undefined,
      availability: availability.trim() || undefined,
    });
    onSaved();
  });

  return (
    <div className="mt-5 grid max-w-2xl gap-4">
      <Field
        label="Headline"
        hint="One line. What you are called in when the firm needs you."
        maxLength={200}
        value={headline}
        onChange={(e) => setHeadline(e.target.value)}
      />
      <Field
        label="Expertise"
        hint="Separated by commas. These are what the firm searches on."
        value={expertise}
        onChange={(e) => setExpertise(e.target.value)}
      />
      <Field
        label="Availability"
        hint="In your own words — 'two days a week to December' is more use than a date."
        value={availability}
        onChange={(e) => setAvailability(e.target.value)}
      />
      <TextArea
        label="Background"
        hint="Optional, and longer. Read when somebody is deciding between two people."
        rows={4}
        maxLength={4000}
        value={bio}
        onChange={(e) => setBio(e.target.value)}
      />
      <div>
        <Button
          tone="primary"
          loading={save.pending}
          onClick={() => void save.invoke()}
        >
          Save your entry
        </Button>
      </div>
      {save.refusal && (
        <p className="border-l-2 border-danger pl-4 text-sm text-ink">
          {save.refusal.message}
        </p>
      )}
    </div>
  );
}

/**
 * What the firm has written about this person.
 *
 * **Never shown to the person it is about**, and that is the database's doing
 * rather than this component's: `note_read` (051) requires
 * `app.on_my_bench(subject)` AND `subject_user_id <> app.uid()`, so a
 * consultant asking gets an empty list and the route they would ask through is
 * not on their rail. Nothing here checks a role.
 *
 * **There is no edit and no delete, by construction.** `consultant_note` has
 * no update or delete policy and 051 revokes both privileges, because a note
 * is the kind of record a staffing decision later rests on and one that can be
 * rewritten afterwards cannot be relied on to say what it said at the time. A
 * note that was wrong is answered by writing another, which is why the form
 * stays open underneath the list.
 */
function Notes({
  subjectId,
  name,
  onAdded,
}: {
  subjectId: string;
  name: string;
  onAdded: () => void;
}) {
  const notes = useResource<BenchNote[]>(
    () => api.get<BenchNote[]>(`/api/people/${subjectId}/notes`),
    [subjectId],
  );
  const [body, setBody] = useState("");

  const { invoke, pending, refusal } = useAction(async () => {
    await api.post(`/api/people/${subjectId}/notes`, { body: body.trim() });
    setBody("");
    notes.reload();
    // The row's note count lives on the bench, so the list behind needs to
    // know. Safe here, unlike on a form: this component is not the one
    // holding what was just written — the server is, and `notes.reload()`
    // above is what fetches it back.
    onAdded();
  });

  const rows = notes.resource.state === "ready" ? notes.resource.data : [];

  return (
    <div className="mt-4 border-l-2 border-line pl-4">
      <Label as="h3">Notes</Label>

      {notes.resource.state === "loading" && <SkeletonRows rows={2} />}
      {notes.resource.state === "failed" && (
        <Failed
          message={notes.resource.message}
          correlationId={notes.resource.correlationId}
        />
      )}

      {notes.resource.state === "ready" && rows.length === 0 && (
        <p className="mt-2 max-w-[62ch] text-sm text-secondary">
          Nothing written about {name.split(" ")[0]} yet. A note here is the
          firm&rsquo;s own record and {name.split(" ")[0]} never sees it.
        </p>
      )}

      {rows.length > 0 && (
        <ul className="mt-3">
          {rows.map((n) => (
            <li key={n.id} className="border-t border-line py-3 first:border-t-0">
              <p className="max-w-[62ch] text-sm leading-[1.2rem] text-ink">
                {n.body}
              </p>
              <p className="mt-1 text-xs text-muted">
                {n.author ?? "—"}
                <span aria-hidden> · </span>
                {day(n.createdAt)}
              </p>
            </li>
          ))}
        </ul>
      )}

      <form
        className="mt-4"
        onSubmit={(e) => {
          e.preventDefault();
          void invoke();
        }}
      >
        <TextArea
          label="Add a note"
          hint="Dated and attributed to you, and it cannot be edited or removed afterwards."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={4000}
        />
        {refusal && (
          <Failed message={refusal.message} correlationId={refusal.correlationId} />
        )}
        <Button
          type="submit"
          tone="quiet"
          className="mt-3"
          loading={pending}
          disabled={!body.trim()}
        >
          Add it
        </Button>
      </form>
    </div>
  );
}
